import React, { useState, useCallback, useRef, useEffect } from 'react';
import { copyToClipboard } from '../../utils/clipboard';
import styled from 'styled-components';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import TooltipBase, { tooltipClasses } from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';

const CustomTooltip = styled(({ className, ...props }: any) => (
  <TooltipBase {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '0.875rem',
  },
});

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.mode === 'dark' ? '#1a1a1a' : '#ffffff'};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  border-bottom: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  margin: 0 8px;
`;

const ToolButton = styled(IconButton)<{ $active?: boolean }>`
  && {
    padding: 6px;
    border-radius: 6px;
    color: ${({ theme, $active }) => $active
      ? (theme.mode === 'dark' ? '#60a5fa' : '#2563eb')
      : (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)')};
    background: ${({ $active }) => $active ? 'rgba(37, 99, 235, 0.1)' : 'transparent'};
    &:hover {
      background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
    }
    &:disabled {
      color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
    }
  }
`;

const FormulaBar = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  gap: 12px;
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)'};
  border-bottom: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
`;

const CellReference = styled.div`
  min-width: 60px;
  padding: 6px 10px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'};
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'};
  border-radius: 4px;
  text-align: center;
`;

const FormulaInput = styled.input`
  flex: 1;
  padding: 6px 10px;
  font-size: 0.9rem;
  font-family: 'Monaco', 'Menlo', monospace;
  color: ${({ theme }) => theme.font_color};
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 4px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.mode === 'dark' ? '#60a5fa' : '#2563eb'};
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;

const Table = styled.table`
  border-collapse: collapse;
  min-width: 100%;
  table-layout: fixed;
`;

const ColumnHeader = styled.th<{ $selected?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 2;
  min-width: 100px;
  max-width: 200px;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'};
  background: ${({ theme, $selected }) => $selected
    ? (theme.mode === 'dark' ? 'rgba(96, 165, 250, 0.2)' : 'rgba(37, 99, 235, 0.1)')
    : (theme.mode === 'dark' ? '#252525' : '#f5f5f5')};
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  cursor: pointer;
  user-select: none;
  &:hover {
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const RowHeader = styled.td<{ $selected?: boolean }>`
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 50px;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  text-align: center;
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  background: ${({ theme, $selected }) => $selected
    ? (theme.mode === 'dark' ? 'rgba(96, 165, 250, 0.2)' : 'rgba(37, 99, 235, 0.1)')
    : (theme.mode === 'dark' ? '#252525' : '#f5f5f5')};
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  cursor: pointer;
  user-select: none;
  &:hover {
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const CornerCell = styled.th`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 3;
  min-width: 50px;
  background: ${({ theme }) => theme.mode === 'dark' ? '#252525' : '#f5f5f5'};
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
`;

const Cell = styled.td<{ $selected?: boolean; $editing?: boolean }>`
  min-width: 100px;
  max-width: 200px;
  height: 32px;
  padding: 0;
  border: 1px solid ${({ theme, $selected }) => $selected
    ? (theme.mode === 'dark' ? '#60a5fa' : '#2563eb')
    : (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)')};
  background: ${({ theme, $selected }) => $selected
    ? (theme.mode === 'dark' ? 'rgba(96, 165, 250, 0.1)' : 'rgba(37, 99, 235, 0.05)')
    : (theme.mode === 'dark' ? '#1a1a1a' : '#ffffff')};
  outline: ${({ $selected }) => $selected ? '2px solid' : 'none'};
  outline-color: ${({ theme }) => theme.mode === 'dark' ? '#60a5fa' : '#2563eb'};
  outline-offset: -1px;
  cursor: cell;
  position: relative;
`;

const CellInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 6px 10px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.font_color};
  background: transparent;
  border: none;
  outline: none;
  font-family: inherit;
`;

const CellDisplay = styled.div`
  width: 100%;
  height: 100%;
  padding: 6px 10px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.font_color};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'};
  border-top: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
`;

const StatsSection = styled.div`
  display: flex;
  gap: 16px;
`;

const StatItem = styled.span`
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
`;

interface CellPosition {
  row: number;
  col: number;
}

interface HistoryState {
  data: string[][];
  headers: string[];
}

interface SpreadsheetEditorProps {
  initialHeaders: string[];
  initialData: string[][];
  onChange?: (headers: string[], data: string[][]) => void;
}

const getColumnLabel = (index: number): string => {
  let label = '';
  let i = index;
  while (i >= 0) {
    label = String.fromCharCode(65 + (i % 26)) + label;
    i = Math.floor(i / 26) - 1;
  }
  return label;
};

const SpreadsheetEditor: React.FC<SpreadsheetEditorProps> = ({
  initialHeaders,
  initialData,
  onChange,
}) => {
  const [headers, setHeaders] = useState<string[]>(initialHeaders);
  const [data, setData] = useState<string[][]>(initialData);
  const [selectedCell, setSelectedCell] = useState<CellPosition | null>(null);
  const [editingCell, setEditingCell] = useState<CellPosition | null>(null);
  const [formulaValue, setFormulaValue] = useState('');
  const [history, setHistory] = useState<HistoryState[]>([{ data: initialData, headers: initialHeaders }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [clipboard, setClipboard] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; type: 'row' | 'col' | 'cell'; index: number } | null>(null);

  const tableRef = useRef<HTMLTableElement>(null);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const saveToHistory = useCallback((newHeaders: string[], newData: string[][]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ headers: newHeaders, data: newData });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const updateData = useCallback((newHeaders: string[], newData: string[][]) => {
    setHeaders(newHeaders);
    setData(newData);
    saveToHistory(newHeaders, newData);
    onChange?.(newHeaders, newData);
  }, [onChange, saveToHistory]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setHeaders(history[newIndex].headers);
      setData(history[newIndex].data);
      onChange?.(history[newIndex].headers, history[newIndex].data);
    }
  }, [history, historyIndex, onChange]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setHeaders(history[newIndex].headers);
      setData(history[newIndex].data);
      onChange?.(history[newIndex].headers, history[newIndex].data);
    }
  }, [history, historyIndex, onChange]);

  const handleCellClick = useCallback((row: number, col: number) => {
    setSelectedCell({ row, col });
    if (row === -1) {
      setFormulaValue(headers[col] || '');
    } else {
      setFormulaValue(data[row]?.[col] || '');
    }
  }, [data, headers]);

  const handleCellDoubleClick = useCallback((row: number, col: number) => {
    setEditingCell({ row, col });
    setSelectedCell({ row, col });
  }, []);

  const handleCellChange = useCallback((row: number, col: number, value: string) => {
    if (row === -1) {
      const newHeaders = [...headers];
      newHeaders[col] = value;
      setHeaders(newHeaders);
      setFormulaValue(value);
    } else {
      const newData = data.map((r, i) => i === row ? [...r] : r);
      while (newData[row].length <= col) {
        newData[row].push('');
      }
      newData[row][col] = value;
      setData(newData);
      setFormulaValue(value);
    }
  }, [data, headers]);

  const handleCellBlur = useCallback((row: number, col: number) => {
    setEditingCell(null);
    if (row === -1) {
      updateData(headers, data);
    } else {
      updateData(headers, data);
    }
  }, [data, headers, updateData]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, row: number, col: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCellBlur(row, col);
      if (row < data.length - 1) {
        setSelectedCell({ row: row + 1, col });
        setFormulaValue(data[row + 1]?.[col] || '');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      handleCellBlur(row, col);
      if (col < headers.length - 1) {
        setSelectedCell({ row, col: col + 1 });
        if (row === -1) {
          setFormulaValue(headers[col + 1] || '');
        } else {
          setFormulaValue(data[row]?.[col + 1] || '');
        }
      }
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  }, [data, handleCellBlur, headers]);

  const handleFormulaChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormulaValue(value);
    if (selectedCell) {
      handleCellChange(selectedCell.row, selectedCell.col, value);
    }
  }, [handleCellChange, selectedCell]);

  const handleFormulaKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && selectedCell) {
      handleCellBlur(selectedCell.row, selectedCell.col);
    }
  }, [handleCellBlur, selectedCell]);

  const handleCopy = useCallback(() => {
    if (selectedCell) {
      const value = selectedCell.row === -1
        ? headers[selectedCell.col]
        : data[selectedCell.row]?.[selectedCell.col];
      setClipboard(value || '');
      void copyToClipboard(value || '');
    }
  }, [selectedCell, headers, data]);

  const handlePaste = useCallback(async () => {
    if (selectedCell) {
      try {
        const text = await navigator.clipboard?.readText() || clipboard;
        if (text) {
          handleCellChange(selectedCell.row, selectedCell.col, text);
          updateData(headers, data);
        }
      } catch {
        if (clipboard) {
          handleCellChange(selectedCell.row, selectedCell.col, clipboard);
          updateData(headers, data);
        }
      }
    }
  }, [selectedCell, clipboard, handleCellChange, updateData, headers, data]);

  const addRow = useCallback((afterIndex?: number) => {
    const newRow = new Array(headers.length).fill('');
    const newData = [...data];
    if (afterIndex !== undefined) {
      newData.splice(afterIndex + 1, 0, newRow);
    } else {
      newData.push(newRow);
    }
    updateData(headers, newData);
  }, [data, headers, updateData]);

  const deleteRow = useCallback((index: number) => {
    if (data.length > 1) {
      const newData = data.filter((_, i) => i !== index);
      updateData(headers, newData);
      setSelectedCell(null);
    }
  }, [data, headers, updateData]);

  const addColumn = useCallback((afterIndex?: number) => {
    const newHeaders = [...headers];
    const insertIndex = afterIndex !== undefined ? afterIndex + 1 : headers.length;
    newHeaders.splice(insertIndex, 0, `Coluna ${insertIndex + 1}`);

    const newData = data.map(row => {
      const newRow = [...row];
      newRow.splice(insertIndex, 0, '');
      return newRow;
    });

    updateData(newHeaders, newData);
  }, [data, headers, updateData]);

  const deleteColumn = useCallback((index: number) => {
    if (headers.length > 1) {
      const newHeaders = headers.filter((_, i) => i !== index);
      const newData = data.map(row => row.filter((_, i) => i !== index));
      updateData(newHeaders, newData);
      setSelectedCell(null);
    }
  }, [data, headers, updateData]);

  const handleContextMenu = useCallback((e: React.MouseEvent, type: 'row' | 'col' | 'cell', index: number) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, type, index });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const getCellReference = useCallback((): string => {
    if (!selectedCell) return '';
    const col = getColumnLabel(selectedCell.col);
    const row = selectedCell.row === -1 ? 'Header' : (selectedCell.row + 1).toString();
    return `${col}${row}`;
  }, [selectedCell]);

  const calculateStats = useCallback(() => {
    if (!selectedCell || selectedCell.row === -1) return null;

    const value = data[selectedCell.row]?.[selectedCell.col];
    const num = parseFloat(value);

    if (!isNaN(num)) {
      const columnValues = data
        .map(row => parseFloat(row[selectedCell.col]))
        .filter(v => !isNaN(v));

      if (columnValues.length > 0) {
        const sum = columnValues.reduce((a, b) => a + b, 0);
        const avg = sum / columnValues.length;
        return { sum: sum.toFixed(2), avg: avg.toFixed(2), count: columnValues.length };
      }
    }
    return null;
  }, [selectedCell, data]);

  const stats = calculateStats();

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        handleCopy();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        handlePaste();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleUndo, handleRedo, handleCopy, handlePaste]);

  return (
    <EditorContainer>
      <Toolbar>
        <CustomTooltip title="Desfazer (Ctrl+Z)" placement="bottom">
          <span>
            <ToolButton onClick={handleUndo} disabled={historyIndex === 0}>
              <UndoIcon fontSize="small" />
            </ToolButton>
          </span>
        </CustomTooltip>
        <CustomTooltip title="Refazer (Ctrl+Y)" placement="bottom">
          <span>
            <ToolButton onClick={handleRedo} disabled={historyIndex === history.length - 1}>
              <RedoIcon fontSize="small" />
            </ToolButton>
          </span>
        </CustomTooltip>

        <ToolbarDivider />

        <CustomTooltip title="Copiar (Ctrl+C)" placement="bottom">
          <span>
            <ToolButton onClick={handleCopy} disabled={!selectedCell}>
              <ContentCopyIcon fontSize="small" />
            </ToolButton>
          </span>
        </CustomTooltip>
        <CustomTooltip title="Colar (Ctrl+V)" placement="bottom">
          <span>
            <ToolButton onClick={handlePaste} disabled={!selectedCell}>
              <ContentPasteIcon fontSize="small" />
            </ToolButton>
          </span>
        </CustomTooltip>

        <ToolbarDivider />

        <CustomTooltip title="Adicionar linha" placement="bottom">
          <ToolButton onClick={() => addRow()}>
            <AddIcon fontSize="small" />
          </ToolButton>
        </CustomTooltip>
        <CustomTooltip title="Adicionar coluna" placement="bottom">
          <ToolButton onClick={() => addColumn()}>
            <AddIcon fontSize="small" style={{ transform: 'rotate(90deg)' }} />
          </ToolButton>
        </CustomTooltip>
      </Toolbar>

      <FormulaBar>
        <CellReference>{getCellReference() || '-'}</CellReference>
        <FormulaInput
          value={formulaValue}
          onChange={handleFormulaChange}
          onKeyDown={handleFormulaKeyDown}
          placeholder="Valor da célula"
        />
      </FormulaBar>

      <TableContainer>
        <Table ref={tableRef}>
          <thead>
            <tr>
              <CornerCell />
              {headers.map((header, colIndex) => (
                <ColumnHeader
                  key={colIndex}
                  $selected={selectedCell?.col === colIndex}
                  onClick={() => handleCellClick(-1, colIndex)}
                  onDoubleClick={() => handleCellDoubleClick(-1, colIndex)}
                  onContextMenu={(e) => handleContextMenu(e, 'col', colIndex)}
                >
                  {editingCell?.row === -1 && editingCell?.col === colIndex ? (
                    <CellInput
                      autoFocus
                      value={header}
                      onChange={(e) => handleCellChange(-1, colIndex, e.target.value)}
                      onBlur={() => handleCellBlur(-1, colIndex)}
                      onKeyDown={(e) => handleKeyDown(e, -1, colIndex)}
                    />
                  ) : (
                    header || getColumnLabel(colIndex)
                  )}
                </ColumnHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <RowHeader
                  $selected={selectedCell?.row === rowIndex}
                  onContextMenu={(e) => handleContextMenu(e, 'row', rowIndex)}
                >
                  {rowIndex + 1}
                </RowHeader>
                {headers.map((_, colIndex) => {
                  const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
                  const isEditing = editingCell?.row === rowIndex && editingCell?.col === colIndex;
                  const cellValue = row[colIndex] || '';

                  return (
                    <Cell
                      key={colIndex}
                      $selected={isSelected}
                      $editing={isEditing}
                      onClick={() => handleCellClick(rowIndex, colIndex)}
                      onDoubleClick={() => handleCellDoubleClick(rowIndex, colIndex)}
                      onContextMenu={(e) => handleContextMenu(e, 'cell', rowIndex * 1000 + colIndex)}
                    >
                      {isEditing ? (
                        <CellInput
                          autoFocus
                          value={cellValue}
                          onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                          onBlur={() => handleCellBlur(rowIndex, colIndex)}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                          ref={(el) => {
                            if (el) inputRefs.current.set(`${rowIndex}-${colIndex}`, el);
                          }}
                        />
                      ) : (
                        <CellDisplay>{cellValue}</CellDisplay>
                      )}
                    </Cell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      <StatusBar>
        <span>{data.length} linhas, {headers.length} colunas</span>
        {stats && (
          <StatsSection>
            <StatItem>Soma: {stats.sum}</StatItem>
            <StatItem>Média: {stats.avg}</StatItem>
            <StatItem>Contagem: {stats.count}</StatItem>
          </StatsSection>
        )}
      </StatusBar>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
      >
        {contextMenu?.type === 'row' && (
          <>
            <MenuItem onClick={() => { addRow(contextMenu.index); handleCloseContextMenu(); }}>
              <AddIcon fontSize="small" style={{ marginRight: 8 }} />
              Inserir linha abaixo
            </MenuItem>
            <MenuItem onClick={() => { deleteRow(contextMenu.index); handleCloseContextMenu(); }}>
              <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
              Excluir linha
            </MenuItem>
          </>
        )}
        {contextMenu?.type === 'col' && (
          <>
            <MenuItem onClick={() => { addColumn(contextMenu.index); handleCloseContextMenu(); }}>
              <AddIcon fontSize="small" style={{ marginRight: 8 }} />
              Inserir coluna à direita
            </MenuItem>
            <MenuItem onClick={() => { deleteColumn(contextMenu.index); handleCloseContextMenu(); }}>
              <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
              Excluir coluna
            </MenuItem>
          </>
        )}
        {contextMenu?.type === 'cell' && (
          <>
            <MenuItem onClick={() => { handleCopy(); handleCloseContextMenu(); }}>
              <ContentCopyIcon fontSize="small" style={{ marginRight: 8 }} />
              Copiar
            </MenuItem>
            <MenuItem onClick={() => { handlePaste(); handleCloseContextMenu(); }}>
              <ContentPasteIcon fontSize="small" style={{ marginRight: 8 }} />
              Colar
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              const rowIndex = Math.floor(contextMenu.index / 1000);
              addRow(rowIndex);
              handleCloseContextMenu();
            }}>
              <AddIcon fontSize="small" style={{ marginRight: 8 }} />
              Inserir linha abaixo
            </MenuItem>
            <MenuItem onClick={() => {
              const colIndex = contextMenu.index % 1000;
              addColumn(colIndex);
              handleCloseContextMenu();
            }}>
              <AddIcon fontSize="small" style={{ marginRight: 8 }} />
              Inserir coluna à direita
            </MenuItem>
          </>
        )}
      </Menu>
    </EditorContainer>
  );
};

export default SpreadsheetEditor;
